import { useState } from "react"
import {
  Box,
  Checkbox,
  Chip,
  Fade,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/AddOutlined"
import DragIcon from "@mui/icons-material/DragIndicator"
import OpenIcon from "@mui/icons-material/OpenInFullOutlined"

import { taskApi } from "../../api"
import { usePermissions } from "../../permissions"
import { MOTION, transition } from "../../theme"
import { applyOrder, useDragReorder } from "../../components/useDragReorder"

/**
 * The steps inside one task.
 *
 * ── Why a checklist and not a nested board ──────────────────────────────────
 *
 * A sub-task is nearly always a step: "chase the signed form", "open the
 * carrier ticket". What people want from it is to tick it off and see how much
 * is left, not to give it a priority and a due date — and a control that offers
 * all of that makes adding one feel like filing a ticket, which is exactly how
 * sub-tasks end up unused.
 *
 * So adding one is a line of text and Enter. Everything else a sub-task can
 * carry is still there, in its own panel, one click away.
 *
 * ── Why the tick writes a status and not a flag ─────────────────────────────
 *
 * A sub-task is a task. Ticking it moves it to the board's terminal column,
 * which means it leaves the open counts, stops its SLA and shows up in the
 * report — all the things that already happen when work finishes. A separate
 * `is_done` boolean beside a status column is two truths about the same thing,
 * and they disagree within a week.
 */
export default function SubtaskList({
  task,
  subtasks: givenSubtasks = [],
  statuses = [],
  onChanged,
  onOpen,
  dense = false,
  /** The parent is refetching these rows. See the note on `working` below. */
  refreshing = false,
}) {
  const { can } = usePermissions()
  const [draft, setDraft] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  /*
   * Which rows are mid-save, and what they are about to become.
   *
   * ── Why the tick is local before it is true ──────────────────────────────
   *
   * Ticking a box is the one interaction here, and it is the one that felt
   * broken: the click went to the server, the server answered, the parent
   * refetched, and only then did the box change. A checkbox that does not move
   * when you click it gets clicked again.
   *
   * So the row shows what you asked for immediately and the request catches up.
   * If it fails the entry is dropped and the row snaps back to the truth, with
   * the error said out loud — which is the only honest version of an optimistic
   * update.
   */
  const [pending, setPending] = useState({})

  /*
   * The order the steps were dragged into, held here until the parent's copy
   * catches up. Applied on top of whatever the parent passes, so a step added
   * meanwhile still shows (at the end) instead of vanishing behind a stale
   * list of ids.
   */
  const [orderIds, setOrderIds] = useState(null)
  const [reordering, setReordering] = useState(false)
  const subtasks = applyOrder(givenSubtasks, orderIds)
  const reorder = async (ids) => {
    setOrderIds(ids)
    setReordering(true)
    setError(null)
    try {
      await taskApi.reorder(ids)
    } catch (err) {
      setOrderIds(null)
      setError(err.userMessage || "That order could not be saved.")
    } finally {
      setReordering(false)
    }
  }
  const stepDrag = useDragReorder(subtasks, { onReorder: reorder, enabled: can("tasks", "update") })

  /* Anything in flight, from here or from the parent. One flag so the bar has
     one reason to be there rather than two that can disagree. */
  const working = refreshing || saving || reordering || Object.keys(pending).length > 0

  const doneStatus = statuses.find((status) => status.is_terminal && status.name === "done") || statuses.find((s) => s.is_terminal)
  const openStatus = statuses.find((status) => !status.is_terminal && status.name === "todo") || statuses.find((s) => !s.is_terminal)

  const total = subtasks.length

  const add = async () => {
    const title = draft.trim()
    if (!title || saving) return
    setSaving(true)
    setError(null)
    try {
      await taskApi.create({ client_id: task.client_id, parent_task_id: task.id, title })
      setDraft("")
      await onChanged?.()
    } catch (err) {
      setError(err.userMessage || "That step could not be added.")
    } finally {
      setSaving(false)
    }
  }

  const toggle = async (subtask) => {
    const next = subtask.is_terminal ? openStatus : doneStatus
    if (!next || pending[subtask.id] !== undefined) return

    const wanted = Boolean(next.is_terminal)
    setPending((current) => ({ ...current, [subtask.id]: wanted }))
    setError(null)
    try {
      await taskApi.update(subtask.id, { status_id: next.id })
      /* Awaited, so the row stays in `pending` — showing what was asked for —
         until the refreshed rows arrive. Dropping it the moment the PATCH
         returns puts the old value back for one frame, which is the flicker in
         a different place. */
      await onChanged?.()
    } catch (err) {
      setError(err.userMessage || "That step could not be updated.")
    } finally {
      setPending((current) => {
        const rest = { ...current }
        delete rest[subtask.id]
        return rest
      })
    }
  }

  /** What a row should show: what was asked for, or what the server last said. */
  const isDone = (subtask) => (pending[subtask.id] !== undefined ? pending[subtask.id] : Boolean(subtask.is_terminal))

  /* Counted after `isDone` is defined, so "3 of 5" moves with the tick rather
     than a round trip behind it. */
  const done = subtasks.filter((row) => isDone(row)).length

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: "0.05em" }}>
          SUB-TASKS
        </Typography>
        {total > 0 ? (
          <>
            <Chip
              size="small"
              label={`${done} of ${total} done`}
              color={done === total ? "success" : "default"}
              variant={done === total ? "filled" : "outlined"}
              sx={{ height: 19, fontSize: 10 }}
            />
            <LinearProgress
              variant="determinate"
              value={total ? (done / total) * 100 : 0}
              sx={{ flex: 1, height: 5, borderRadius: 3, maxWidth: 160 }}
            />
          </>
        ) : null}
      </Stack>

      {/*
        * The bar sits over the list; the list never leaves.
        *
        * Ticking a step used to take the rows away and put a loader where they
        * had been, so the one thing somebody was looking at — the step they just
        * ticked, and the ones under it — jumped out from under the pointer and
        * came back a moment later somewhere slightly different. The rows stay
        * mounted and a two-pixel bar appears across the top of them instead:
        * the same information that the list is catching up, in a place that
        * costs nothing to show.
        *
        * The empty line is held back while a refresh is in flight for the same
        * reason — "No steps yet" under a loading bar is a statement that is
        * about to be contradicted.
        */}
      <Box sx={{ position: "relative", mb: 1, minHeight: working && total === 0 ? 20 : undefined }}>
        {/* Mounted only while something is in flight. Left in place at zero
            opacity it would keep its animation running on every expanded task
            on the screen, for a bar nobody can see. */}
        {working ? (
          <Fade in timeout={120}>
            <LinearProgress
              aria-label="Saving"
              sx={{
                position: "absolute",
                top: -3,
                left: 0,
                right: 0,
                height: 2,
                borderRadius: 2,
                pointerEvents: "none",
              }}
            />
          </Fade>
        ) : null}

        {total === 0 ? (
          refreshing ? null : (
            <Typography variant="body2" color="text.secondary">
              No steps yet. Break this into the things that have to happen.
            </Typography>
          )
        ) : (
          <Stack spacing={0}>
            {subtasks.map((subtask) => (
              <Stack
                key={subtask.id}
                direction="row"
                alignItems="center"
                spacing={0.5}
                {...stepDrag.rowProps(subtask)}
                sx={{
                  py: dense ? 0.1 : 0.25,
                  borderRadius: 1,
                  transition: transition("background-color", MOTION.fast),
                  "&:hover": { bgcolor: "action.hover" },
                  "&:hover .subtask-open, &:hover .subtask-drag, & .subtask-drag:focus-visible": { opacity: 1 },
                  ...stepDrag.rowSx(subtask),
                }}
              >
                {can("tasks", "update") && subtasks.length > 1 ? (
                  <IconButton
                    size="small"
                    className="subtask-drag"
                    aria-label={`Move "${subtask.title}"`}
                    title="Drag to reorder · or focus and use ↑ ↓"
                    {...stepDrag.handleProps(subtask)}
                    sx={{ p: 0.2, cursor: "grab", opacity: 0, transition: transition("opacity", MOTION.fast) }}
                  >
                    <DragIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                ) : null}
                <Checkbox
                  size="small"
                  checked={isDone(subtask)}
                  onChange={() => toggle(subtask)}
                  disabled={!can("tasks", "update")}
                  inputProps={{ "aria-label": `Mark "${subtask.title}" ${isDone(subtask) ? "not done" : "done"}` }}
                  sx={{ p: 0.5 }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    textDecoration: isDone(subtask) ? "line-through" : "none",
                    color: isDone(subtask) ? "text.disabled" : "text.primary",
                    transition: transition(["color", "text-decoration-color"], MOTION.fast),
                  }}
                  noWrap
                >
                  {subtask.title}
                </Typography>
                {subtask.assignee_name ? (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 110 }}>
                    {subtask.assignee_name}
                  </Typography>
                ) : null}
                {onOpen ? (
                  <Tooltip title="Open this step">
                    <IconButton
                      size="small"
                      className="subtask-open"
                      onClick={() => onOpen(subtask.id)}
                      aria-label={`Open ${subtask.task_code || subtask.title}`}
                      sx={{ opacity: 0, transition: transition("opacity", MOTION.fast), p: 0.4 }}
                    >
                      <OpenIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Stack>
            ))}
          </Stack>
        )}
      </Box>

      {can("tasks", "create") ? (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <AddIcon sx={{ fontSize: 16, color: "text.disabled" }} />
          <TextField
            variant="standard"
            placeholder="Add a step and press Enter"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault()
                add()
              }
            }}
            disabled={saving}
            fullWidth
            slotProps={{ input: { disableUnderline: false } }}
            sx={{ "& .MuiInputBase-input": { fontSize: 14, py: 0.25 } }}
          />
        </Stack>
      ) : null}

      {error ? (
        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  )
}
